
// Deployment trigger: 2026-04-24
import React, { useState, useEffect, useRef } from 'react';
import { Gift, ArrowDownCircle, Trophy } from 'lucide-react';

interface GiftRainProps {
  onWin: () => void;
  isLightMode?: boolean;
}

const GiftRain: React.FC<GiftRainProps> = ({ onWin, isLightMode }) => {
  const [score, setScore] = useState(0);
  const [gifts, setGifts] = useState<{ id: number; left: number; top: number; speed: number }[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const TARGET_SCORE = 5;

  useEffect(() => {
    const interval = setInterval(() => {
      setGifts(prev => [
        ...prev.map(g => ({ ...g, top: g.top + g.speed })),
        { id: Date.now(), left: Math.random() * 80 + 10, top: -10, speed: Math.random() * 2 + 1 }
      ].filter(g => g.top < 100));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const catchGift = (id: number) => {
    setGifts(prev => prev.filter(g => g.id !== id));
    setScore(s => {
      const newScore = s + 1;
      if (newScore >= TARGET_SCORE) onWin();
      return newScore;
    });
  };

  return (
    <div className="animate-in fade-in duration-500 h-full flex flex-col">
      <div className="text-center mb-8">
        <ArrowDownCircle className={`w-12 h-12 ${isLightMode ? 'text-indigo-600' : 'text-indigo-400'} mx-auto mb-4 animate-bounce`} />
        <h3 className={`${isLightMode ? 'text-slate-800' : 'text-white'} font-black text-xl uppercase tracking-widest italic leading-none`}>Gift Rain</h3>
        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest mt-2">Catch {TARGET_SCORE} gifts to win!</p>
      </div>

      <div ref={containerRef} className={`relative flex-1 rounded-3xl border min-h-[300px] overflow-hidden cursor-crosshair ${isLightMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-900/50 border-white/5'}`}>
        {gifts.map(g => (
          <button
            key={g.id}
            onMouseDown={() => catchGift(g.id)}
            onTouchStart={() => catchGift(g.id)}
            className="absolute p-3 bg-indigo-600 rounded-xl text-white shadow-lg transition-transform hover:scale-125 active:scale-90"
            style={{ left: `${g.left}%`, top: `${g.top}%` }}
          >
            <Gift className="w-5 h-5" />
          </button>
        ))}
        
        <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 backdrop-blur-md px-4 py-2 rounded-full border flex items-center gap-2 ${isLightMode ? 'bg-white/80 border-slate-200' : 'bg-black/40 border-white/10'}`}>
          <Trophy className="w-4 h-4 text-amber-400" />
          <span className={`${isLightMode ? 'text-slate-800' : 'text-white'} font-black text-sm tracking-widest`}>{score} / {TARGET_SCORE}</span>
        </div>
      </div>
    </div>
  );
};

export default GiftRain;
